import React, { useState, useEffect, useCallback } from 'react';

// =========================================================================
// Configuration
// =========================================================================
// NOTE: Assuming your backend is running on port 5000 as per your server.js
const API_BASE_URL = 'http://localhost:5000/api';

// =========================================================================
// 1. UTILITY COMPONENTS
// =========================================================================

/**
 * NeonGlitchEffect: Applies a simulated neon glow and subtle glitch animation.
 */
const NeonGlitchEffect = ({ children, className = '' }) => (
    <div className={`relative ${className}`}>
        {children}
        <style jsx>{`
            .glitch-text {
                text-shadow: 0 0 5px rgba(255, 0, 255, 0.7), 0 0 15px rgba(0, 255, 255, 0.5), 0 0 20px rgba(255, 0, 255, 0.3);
                animation: neon-flicker 1.5s infinite alternate;
            }
            @keyframes neon-flicker {
                0%, 19.9% { text-shadow: 0 0 5px #ff00ff, 0 0 10px #00ffff; }
                20%, 20.9% { text-shadow: none; }
                21%, 29.9% { text-shadow: 0 0 5px #ff00ff, 0 0 10px #00ffff; }
                30%, 30.9% { text-shadow: 0 0 10px #00ffff; }
                40%, 40.9% { text-shadow: 0 0 5px #ff00ff, 0 0 15px #00ffff; }
                41%, 100% { text-shadow: 0 0 5px #ff00ff, 0 0 10px #00ffff; }
            }
        `}</style>
    </div>
);

/**
 * MessageToast: Simple component for showing API success/error messages.
 */
const MessageToast = ({ message, type, onClose }) => {
    if (!message) return null;
    
    const colors = {
        success: 'bg-green-700 border-green-400',
        error: 'bg-red-700 border-red-400',
    };

    return (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl border-l-4 ${colors[type]}`}>
            <div className="flex items-center">
                <p className="text-white text-sm font-medium">{message}</p>
                <button 
                    onClick={onClose} 
                    className="ml-4 text-white hover:text-gray-200 transition-colors"
                    aria-label="Close"
                >
                    &times;
                </button>
            </div>
        </div>
    );
};


// =========================================================================
// 2. ScheduleForm Component
// =========================================================================

/**
 * ScheduleForm: The main form for scheduling a new GlitchWish.
 */
const ScheduleForm = ({ onScheduleSuccess }) => {
    const initialState = {
        recipient: '',
        platform: 'Email',
        date: new Date().toISOString().split('T')[0], // Today's date YYYY-MM-DD
        time: '12:00', // Default time
        message: '',
        glitchTheme: 'Neon Glitch'
    };
    const [formData, setFormData] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState({ message: '', type: '' });

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    // Function to clear the toast message
    const clearToast = () => setToast({ message: '', type: '' });

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        clearToast();

        try {
            const response = await fetch(`${API_BASE_URL}/wishes/schedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle bad response status (4xx, 5xx)
                throw new Error(data.message || 'Failed to schedule wish.');
            }

            // Success
            setToast({ message: data.message, type: 'success' });
            setFormData(initialState); // Reset form
            onScheduleSuccess(); // Refresh dashboard list

        } catch (error) {
            console.error('Scheduling Error:', error);
            setToast({ message: error.message || 'An unknown error occurred.', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const themes = ['Neon Glitch', 'Pixelated Static', 'Data Corruption', 'Hacked Grid'];
    const platforms = ['Email', 'SMS', 'WhatsApp (API)', 'Slack'];

    return (
        <div className="p-6 bg-gray-900/70 border border-purple-500 rounded-xl shadow-2xl backdrop-blur-sm mx-auto max-w-lg">
            <h2 className="text-2xl font-bold text-white mb-6 text-center glitch-text">Schedule A GlitchWish</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* Recipient Input */}
                <div>
                    <label htmlFor="recipient" className="block text-sm font-medium text-purple-300">Recipient (Email/Phone/ID)</label>
                    <input
                        type="text"
                        name="recipient"
                        id="recipient"
                        value={formData.recipient}
                        onChange={handleChange}
                        required
                        placeholder="user@example.com"
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                    />
                </div>

                {/* Platform Selection */}
                <div>
                    <label htmlFor="platform" className="block text-sm font-medium text-purple-300">Delivery Platform</label>
                    <select
                        name="platform"
                        id="platform"
                        value={formData.platform}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                    >
                        {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>

                {/* Date and Time */}
                <div className="flex space-x-4">
                    <div className="flex-1">
                        <label htmlFor="date" className="block text-sm font-medium text-purple-300">Schedule Date</label>
                        <input
                            type="date"
                            name="date"
                            id="date"
                            value={formData.date}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="time" className="block text-sm font-medium text-purple-300">Schedule Time</label>
                        <input
                            type="time"
                            name="time"
                            id="time"
                            value={formData.time}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                        />
                    </div>
                </div>

                {/* Message Textarea */}
                <div>
                    <label htmlFor="message" className="block text-sm font-medium text-purple-300">Glitch Message Content</label>
                    <textarea
                        name="message"
                        id="message"
                        rows="3"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Type your slightly corrupted message here..."
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400 resize-none"
                    ></textarea>
                </div>

                {/* Theme Selection */}
                <div>
                    <label htmlFor="glitchTheme" className="block text-sm font-medium text-purple-300">Visual Theme</label>
                    <select
                        name="glitchTheme"
                        id="glitchTheme"
                        value={formData.glitchTheme}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 text-cyan-300 border border-purple-500 rounded-md shadow-inner focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                    >
                        {themes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                
                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 mt-6 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition duration-150 ease-in-out transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg neon-flicker-button"
                >
                    {isLoading ? 'SCHEDULING...' : 'SCHEDULE GLITCHWISH'}
                </button>
            </form>
            <MessageToast {...toast} onClose={clearToast} />
            <style jsx>{`
                .neon-flicker-button {
                    box-shadow: 0 0 10px rgba(255, 0, 255, 0.5), 0 0 20px rgba(0, 255, 255, 0.3);
                }
                .neon-flicker-button:hover {
                    box-shadow: 0 0 15px rgba(255, 0, 255, 0.8), 0 0 30px rgba(0, 255, 255, 0.6);
                }
            `}</style>
        </div>
    );
};


// =========================================================================
// 3. Dashboard Component
// =========================================================================

/**
 * WishCard: Displays a single scheduled wish item.
 */
const WishCard = ({ wish }) => {
    // Format the date/time nicely
    const scheduledTime = new Date(wish.scheduledDate).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', 
        hour: '2-digit', minute: '2-digit'
    });
    
    // Status color mapping
    const statusColors = {
        'Scheduled': 'text-yellow-400 border-yellow-400',
        'Sent': 'text-green-400 border-green-400',
        'Failed': 'text-red-400 border-red-400',
    };

    return (
        <div className="p-4 bg-gray-800/80 border border-cyan-500 rounded-lg shadow-xl hover:shadow-cyan-500/30 transition duration-300">
            <p className="text-xl font-semibold text-white mb-2">{wish.recipient}</p>
            
            <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-purple-300">Platform: {wish.platform}</span>
                <span className={`px-2 py-0.5 text-xs font-mono border rounded-full ${statusColors[wish.status] || 'text-gray-400 border-gray-400'}`}>
                    {wish.status}
                </span>
            </div>

            <p className="text-xs text-gray-400 mb-3">Scheduled: <span className="font-mono text-cyan-300">{scheduledTime}</span></p>

            <p className="text-sm text-gray-200 italic border-l-2 border-purple-500 pl-3">
                "{wish.message.substring(0, 80)}{wish.message.length > 80 ? '...' : ''}"
            </p>
            <p className="text-xs text-right mt-2 text-purple-400 font-mono">Theme: {wish.glitchTheme}</p>
        </div>
    );
};


/**
 * Dashboard: Displays the list of all scheduled wishes.
 */
const Dashboard = ({ forceRefresh }) => {
    const [wishes, setWishes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchWishes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // NOTE: This assumes you have implemented the GET /api/wishes route
            const response = await fetch(`${API_BASE_URL}/wishes`); 
            
            if (!response.ok) {
                throw new Error('Failed to fetch scheduled wishes from server.');
            }

            const data = await response.json();
            // Sort by scheduled date (newest first)
            const sortedData = data.sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));
            
            setWishes(sortedData);
        } catch (err) {
            console.error("Dashboard Fetch Error:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch data on component mount or when forceRefresh changes (after successful form submission)
    useEffect(() => {
        fetchWishes();
    }, [fetchWishes, forceRefresh]);


    return (
        <div className="p-6 bg-gray-900/70 rounded-xl mx-auto max-w-4xl shadow-2xl">
            <h2 className="text-3xl font-extrabold text-white mb-8 text-center glitch-text">Scheduled GlitchWish Queue</h2>
            
            {isLoading && (
                <p className="text-cyan-400 text-center text-lg animate-pulse">
                    // LOADING DATA CORRUPTION //
                </p>
            )}

            {error && (
                <p className="text-red-500 text-center p-4 border border-red-500 rounded">
                    Error connecting to backend: {error}
                </p>
            )}

            {!isLoading && !error && (
                wishes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {wishes.map(wish => (
                            <WishCard key={wish._id} wish={wish} />
                        ))}
                    </div>
                ) : (
                    <p className="text-purple-300 text-center text-lg">
                        No wishes scheduled yet. Start glitching!
                    </p>
                )
            )}
        </div>
    );
};


// =========================================================================
// 4. MAIN APP COMPONENT
// =========================================================================

const App = () => {
    // Simple page routing using state
    const [page, setPage] = useState('schedule'); // 'schedule' or 'dashboard'
    
    // State to force the dashboard to refresh when a new wish is scheduled
    const [refreshCount, setRefreshCount] = useState(0); 

    const handleScheduleSuccess = () => {
        setPage('dashboard'); // Switch to dashboard after scheduling
        setRefreshCount(prev => prev + 1); // Trigger dashboard refresh
    };
    
    const renderPage = () => {
        switch (page) {
            case 'dashboard':
                return <Dashboard forceRefresh={refreshCount} />;
            case 'schedule':
            default:
                return <ScheduleForm onScheduleSuccess={handleScheduleSuccess} />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white font-sans flex flex-col pt-8 pb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
            {/* Background Glitch Aesthetic */}
            <div className="fixed inset-0 z-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #1f1f3a, #1f1f3a 5px, #10101f 5px, #10101f 10px)' }}></div>
            <div className="fixed inset-0 z-0 opacity-10 bg-gradient-to-br from-purple-900 to-cyan-900 blur-3xl"></div>


            {/* Header / Navigation */}
            <header className="z-10 w-full mb-10">
                <nav className="flex justify-center space-x-4 p-4 mx-auto max-w-4xl bg-gray-800/70 backdrop-blur-md rounded-xl shadow-lg border border-purple-600">
                    <NeonGlitchEffect>
                        <h1 className="text-4xl font-extrabold text-white cursor-pointer glitch-text" onClick={() => setPage('schedule')}>
                            GlitchWish
                        </h1>
                    </NeonGlitchEffect>
                </nav>
                <div className="flex justify-center space-x-6 mt-4 z-10">
                    <NavItem 
                        page="schedule" 
                        currentPage={page} 
                        setPage={setPage}
                        text="Schedule New"
                    />
                    <NavItem 
                        page="dashboard" 
                        currentPage={page} 
                        setPage={setPage}
                        text="View Queue"
                    />
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-grow z-10 px-4">
                {renderPage()}
            </main>
        </div>
    );
}

/**
 * NavItem: Helper component for navigation links.
 */
const NavItem = ({ page, currentPage, setPage, text }) => {
    const isActive = currentPage === page;
    const activeClasses = 'bg-purple-700 text-white shadow-lg shadow-purple-500/50';
    const inactiveClasses = 'bg-gray-700 text-purple-200 hover:bg-gray-600';
    
    return (
        <button
            onClick={() => setPage(page)}
            className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${isActive ? activeClasses : inactiveClasses}`}
        >
            {text}
        </button>
    );
};

export default App;


